import { useMemo } from 'react';

import ProgressBar from '@ramonak/react-progress-bar';
import { useTranslation } from 'react-i18next';

import { SeatBasedUsageResponse } from '@/api/types/backend-dto-types';
import { Skeleton } from '@/new/components/skeleton';
import { SubTitle } from '@/new/components/subtitle';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { cn } from '@/new/lib/utils';

export function SeatCreditUsage({
  data,
  isLoading,
  viewingOtherUser = false,
}: {
  data: SeatBasedUsageResponse | null | undefined;
  isLoading: boolean;
  // Platform usage operators can view another user's seat usage, so the copy can't assume "your".
  viewingOtherUser?: boolean;
}) {
  const { i18n } = useTranslation();
  const title = viewingOtherUser ? 'User Credit Usage' : 'Your Credit Usage';
  const possessive = viewingOtherUser ? 'Their' : 'Your';

  const currentPeriod = data?.periods[data.periods.length - 1] ?? null;

  const usagePercentage = useMemo(() => {
    if (!data || !currentPeriod || !data.creditLimit) return 0;
    return (currentPeriod.totalCredits / data.creditLimit) * 100;
  }, [data, currentPeriod]);

  if (isLoading) {
    return (
      <div>
        <SubTitle text={title} />
        <Skeleton className="mt-4 block h-8 w-full bg-gray-200" />
      </div>
    );
  }

  if (!data) return null;

  // Seat-based user but no applicable periods yet (billing or credit limit too recent)
  if (!currentPeriod) {
    return (
      <div className="w-full">
        <div className="flex items-center gap-1">
          <SubTitle text={title} />
          <TooltipIcon
            side="right"
            tooltipContent="This is a fair usage limit. Consistently exceeding your credit allowance may result in additional charges or restrictions."
          />
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Credit usage will be available once the billing period has started. {possessive} limit is{' '}
          {new Intl.NumberFormat('en-US', { notation: 'compact' }).format(data.creditLimit ?? 0)} credits per month.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-1">
        <SubTitle text={title} />
        <TooltipIcon
          side="right"
          tooltipContent="This is a fair usage limit. Consistently exceeding your credit allowance may result in additional charges or restrictions."
        />
      </div>
      <p className="mt-1 text-sm text-gray-600">{possessive} total credit usage for the current period.</p>

      <div className="relative mt-6">
        <div className="mb-2 flex items-end justify-between">
          <p className="flex h-6 items-end text-xl font-semibold">
            <span
              className={cn(
                currentPeriod.totalCredits > (data.creditLimit ?? 0) && 'text-red-600',
                'animate-in fade-in',
              )}
            >
              {new Intl.NumberFormat(i18n.language).format(currentPeriod.totalCredits)}
            </span>
            <span className="inline-flex items-center gap-1.5 pb-0.25 pl-1 text-base text-gray-500">
              {' '}
              / {new Intl.NumberFormat('en-US', { notation: 'compact' }).format(data.creditLimit ?? 0)} limit
            </span>
          </p>

          <p className="animate-in fade-in mb-1 text-xs text-gray-500">
            resets on{' '}
            <span className="font-semibold">
              {new Intl.DateTimeFormat(i18n.language).format(new Date(currentPeriod.periodEnd))}
            </span>
          </p>
        </div>

        <ProgressBar
          completed={Math.min(usagePercentage, 100)}
          height="10px"
          bgColor={usagePercentage > 100 ? 'var(--color-red-500)' : 'var(--color-teal-400)'}
          baseBgColor="var(--color-gray-200)"
          className="w-full"
          isLabelVisible={false}
          animateOnRender={true}
        />

        <div className="mt-3 flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className={cn('h-3 w-3 rounded-sm', usagePercentage > 100 ? 'bg-red-500' : 'bg-teal-400')}></div>
            <span className="text-gray-500">Credits used</span>
          </div>
        </div>
      </div>
    </div>
  );
}
